import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  DocumentCheckIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { toast } from 'react-hot-toast';

const Approvals = () => {
  const [selectedTab, setSelectedTab] = useState('checkpoints');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [comments, setComments] = useState('');
  const [feedback, setFeedback] = useState({});
  const queryClient = useQueryClient();

  // Fetch pending approvals
  const { data: pendingData, isLoading } = useQuery({
    queryKey: ['pending-approvals'],
    queryFn: async () => {
      const response = await api.get('/api/hitl/pending');
      return response.data;
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  // Approve checkpoint mutation
  const approveMutation = useMutation({
    mutationFn: async ({ checkpointId, comments, feedback }) => {
      const response = await api.post(`/api/hitl/checkpoints/${checkpointId}/approve`, {
        comments,
        feedback
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Checkpoint approved successfully');
      queryClient.invalidateQueries(['pending-approvals']);
      setShowDetailModal(false);
      setComments('');
      setFeedback({});
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to approve checkpoint');
    }
  });

  // Reject checkpoint mutation
  const rejectMutation = useMutation({
    mutationFn: async ({ checkpointId, reason, feedback }) => {
      const response = await api.post(`/api/hitl/checkpoints/${checkpointId}/reject`, {
        reason,
        feedback
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Checkpoint rejected');
      queryClient.invalidateQueries(['pending-approvals']);
      setShowDetailModal(false);
      setComments('');
      setFeedback({});
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to reject checkpoint');
    }
  });

  // Resolve intervention mutation
  const resolveInterventionMutation = useMutation({
    mutationFn: async ({ interventionId, resolution, actions_taken }) => {
      const response = await api.post(`/api/hitl/interventions/${interventionId}/resolve`, {
        resolution,
        actions_taken
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Intervention resolved successfully');
      queryClient.invalidateQueries(['pending-approvals']);
      setShowDetailModal(false);
      setComments('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to resolve intervention');
    }
  });

  const handleApprove = () => {
    if (!selectedItem) return;

    approveMutation.mutate({
      checkpointId: selectedItem.id,
      comments,
      feedback
    });
  };

  const handleReject = () => {
    if (!selectedItem) return;

    rejectMutation.mutate({
      checkpointId: selectedItem.id,
      reason: comments,
      feedback
    });
  };

  const handleResolveIntervention = () => {
    if (!selectedItem) return;

    resolveInterventionMutation.mutate({
      interventionId: selectedItem.id,
      resolution: comments,
      actions_taken: selectedItem.suggested_actions || []
    });
  };

  const openDetailModal = (item, type) => {
    setSelectedItem({ ...item, type });
    setShowDetailModal(true);
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-blue-100 text-blue-800'
    };
    return colors[severity] || colors.medium;
  };

  const getCheckpointTypeIcon = (type) => {
    const icons = {
      PROJECT_INITIATION: FlagIcon,
      PLANNING_APPROVAL: DocumentCheckIcon,
      MILESTONE_REVIEW: CheckCircleIcon,
      QUALITY_GATE: ClockIcon,
      BUDGET_REVIEW: ExclamationTriangleIcon,
      SCOPE_CHANGE_APPROVAL: ChatBubbleLeftRightIcon
    };
    return icons[type] || DocumentCheckIcon;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const checkpoints = pendingData?.checkpoints || [];
  const interventions = pendingData?.interventions || [];
  const totalPending = pendingData?.total_pending || 0;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Approvals & Interventions</h1>
        <p className="text-gray-600 mt-2">
          Review pending checkpoints and intervention requests requiring your attention
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Total Pending</p>
              <p className="text-2xl font-bold text-gray-900">{totalPending}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <DocumentCheckIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Checkpoints</p>
              <p className="text-2xl font-bold text-gray-900">{checkpoints.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-8 w-8 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-600">Interventions</p>
              <p className="text-2xl font-bold text-gray-900">{interventions.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => setSelectedTab('checkpoints')}
              className={`py-4 px-6 font-medium text-sm border-b-2 ${selectedTab === 'checkpoints'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Checkpoints ({checkpoints.length})
            </button>
            <button
              onClick={() => setSelectedTab('interventions')}
              className={`py-4 px-6 font-medium text-sm border-b-2 ${selectedTab === 'interventions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Interventions ({interventions.length})
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {selectedTab === 'checkpoints' && (
            <div className="space-y-4">
              {checkpoints.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-gray-600">No pending checkpoints</p>
                </div>
              ) : (
                checkpoints.map((checkpoint) => {
                  const Icon = getCheckpointTypeIcon(checkpoint.checkpoint_type);
                  return (
                    <div
                      key={checkpoint.id}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => openDetailModal(checkpoint, 'checkpoint')}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4 flex-1">
                          <Icon className="h-6 w-6 text-blue-600 mt-1" />
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900">{checkpoint.title}</h3>
                            <p className="text-sm text-gray-600 mt-1">{checkpoint.description}</p>
                            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                              <span>Project: {checkpoint.project_id}</span>
                              <span>Due: {new Date(checkpoint.due_date).toLocaleDateString('en-GB')}</span>
                              <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">
                                {checkpoint.checkpoint_type}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openDetailModal(checkpoint, 'checkpoint');
                            }}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                          >
                            Review
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {selectedTab === 'interventions' && (
            <div className="space-y-4">
              {interventions.length === 0 ? (
                <div className="text-center py-12">
                  <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-gray-600">No active interventions</p>
                </div>
              ) : (
                interventions.map((intervention) => (
                  <div
                    key={intervention.id}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => openDetailModal(intervention, 'intervention')}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4 flex-1">
                        <ExclamationTriangleIcon className="h-6 w-6 text-orange-600 mt-1" />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-medium text-gray-900">{intervention.title}</h3>
                            <span className={`px-2 py-1 rounded text-xs ${getSeverityColor(intervention.severity)}`}>
                              {intervention.severity.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{intervention.description}</p>
                          <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                            <span>Trigger: {intervention.trigger_type}</span>
                            <span>Created: {new Date(intervention.created_at).toLocaleDateString('en-GB')}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetailModal(intervention, 'intervention');
                        }}
                        className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 text-sm"
                      >
                        Resolve
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedItem && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {selectedItem.type === 'checkpoint' ? 'Checkpoint Review' : 'Resolve Intervention'}
              </h2>

              {selectedItem.type === 'checkpoint' ? (
                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium text-gray-900">Title</h3>
                    <p className="text-gray-600">{selectedItem.title}</p>
                  </div>

                  <div>
                    <h3 className="font-medium text-gray-900">Description</h3>
                    <p className="text-gray-600">{selectedItem.description}</p>
                  </div>

                  <div>
                    <h3 className="font-medium text-gray-900">Checkpoint Type</h3>
                    <span className="inline-block px-3 py-1 rounded bg-blue-100 text-blue-800 text-sm">
                      {selectedItem.checkpoint_type}
                    </span>
                  </div>

                  {selectedItem.ai_recommendation && Object.keys(selectedItem.ai_recommendation).length > 0 && (
                    <div>
                      <h3 className="font-medium text-gray-900 mb-2">AI Recommendation</h3>
                      <div className="bg-blue-50 p-4 rounded">
                        <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                          {JSON.stringify(selectedItem.ai_recommendation, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block font-medium text-gray-900 mb-2">
                      Your Comments
                    </label>
                    <textarea
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      rows={4}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Add your feedback or comments..."
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      onClick={() => {
                        setShowDetailModal(false);
                        setComments('');
                      }}
                      className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleReject}
                      disabled={rejectMutation.isLoading}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                    >
                      {rejectMutation.isLoading ? 'Rejecting...' : 'Reject'}
                    </button>
                    <button
                      onClick={handleApprove}
                      disabled={approveMutation.isLoading}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      {approveMutation.isLoading ? 'Approving...' : 'Approve'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium text-gray-900">Title</h3>
                    <p className="text-gray-600">{selectedItem.title}</p>
                  </div>

                  <div>
                    <h3 className="font-medium text-gray-900">Description</h3>
                    <p className="text-gray-600">{selectedItem.description}</p>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div>
                      <h3 className="font-medium text-gray-900">Severity</h3>
                      <span className={`inline-block px-3 py-1 rounded text-sm ${getSeverityColor(selectedItem.severity)}`}>
                        {selectedItem.severity.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Trigger</h3>
                      <span className="inline-block px-3 py-1 rounded bg-gray-100 text-gray-800 text-sm">
                        {selectedItem.trigger_type}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block font-medium text-gray-900 mb-2">
                      Resolution Notes
                    </label>
                    <textarea
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      rows={4}
                      className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="Describe how you resolved this intervention..."
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      onClick={() => {
                        setShowDetailModal(false);
                        setComments('');
                      }}
                      className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleResolveIntervention}
                      disabled={resolveInterventionMutation.isLoading}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      {resolveInterventionMutation.isLoading ? 'Resolving...' : 'Mark as Resolved'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Approvals;
